// Экран Профиль — портирован из profile-prototype.html (раздел 3 ui-spec.md).
// Данные пока mock — в следующих этапах подключим к реальному state (Zustand)
// и серверным API. Структура и стили один-в-один с прототипом.

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBackButton, useTelegram } from '../telegram/useTelegram';
import { UserAvatar } from '../components/UserAvatar';
import { InfoModal } from '../components/InfoModal';
import { PlayerName } from '../components/PlayerName';
import { SupportModal } from '../components/SupportModal';
import { LanguagePicker } from '../components/LanguagePicker';
import { useStudyMode } from '../study/useStudyMode';
import { useTooltipFlow } from '../tutorial/useTooltipFlow';
import { useTr } from '../i18n/useTr';
import { useGameStore } from '../store/useGameStore';
import { useToast } from '../components/Toast';
import { setSfxVolume, play as playSfx } from '../audio/sfx';
import {
  requestAccountDeletion, cancelAccountDeletion, setAccentColor, getBattlePass,
  setFtueState, getMatchHistory, type MatchHistoryItem
} from '../api/client';
import styles from './ProfileScreen.module.css';

// БП.1: палитра допустимых цветов ника
const ACCENT_PALETTE = ['00F0FF', 'FF2A6D', 'B4FF00', 'D300FF', 'FFAA00', '00FF88', 'FF4444', 'FFFFFF'] as const;

type StatsTab = 'season' | 'lifetime' | 'war';

// ВОЙ.6: агрегированная статистика по Войне Корп — рассчитывается
// клиентом из /api/matches/history (filter mode='corp_war').
interface WarStats {
  matches: number;
  wins: number;
  winrate: number;          // 0-100
  bestStreak: number;        // лучшая серия побед подряд
  totalUsdtEarned: number;   // в USD (cents/100). Только из winnerов.
  totalUsdtLost: number;     // в USD. Из проигрышей.
  netUsdt: number;            // earned - lost
  factionCounts: Record<string, number>; // 'deus' → 5 и т.п.
  topFaction: string;
}

function capitalize(s: string): string {
  if (!s) return '';
  return s[0].toUpperCase() + s.slice(1);
}

function factionDisplayName(f: string, tr: (ru: string, en: string) => string): string {
  switch (f) {
    case 'deus': return tr('Деус', 'Deus');
    case 'syntetics': return tr('Синтетикс', 'Syntetics');
    case 'global': return tr('Глобал', 'Global');
    case 'livlife': return tr('Лив Лайф', 'Liv Life');
    case 'neutral': return tr('Нейтрал', 'Neutral');
    default: return f || '—';
  }
}

function aggregateWarStats(history: MatchHistoryItem[]): WarStats {
  const wars = history.filter(h => h.mode === 'corp_war');
  const matches = wars.length;
  const wins = wars.filter(h => h.result === 'win').length;
  const winrate = matches > 0 ? Math.round((wins / matches) * 100) : 0;

  // Best streak: войны отсортированы desc по дате; идём в обратном
  // (chronological), считаем подряд-победы.
  const chronological = [...wars].reverse();
  let bestStreak = 0;
  let curStreak = 0;
  for (const m of chronological) {
    if (m.result === 'win') {
      curStreak += 1;
      if (curStreak > bestStreak) bestStreak = curStreak;
    } else {
      curStreak = 0;
    }
  }

  // USDT-расчёт: используем match_history.usdtCentsEarned. Для побед
  // в Войне это net выигрыш (acceptedStake × 0.9 после комиссии).
  // Поражения пока не пишут отрицательные значения в usdt_cents_earned
  // (только wins), поэтому потери считаем из стейка (-) — упрощённо
  // показываем как «потенциальные потери если все loss-матчи были по
  // одной ставке». Для точного — нужны транзакции.
  let totalUsdtEarned = 0;
  let totalUsdtLost = 0;
  for (const m of wars) {
    if (m.result === 'win') {
      totalUsdtEarned += (m.usdtCentsEarned || 0) / 100;
    } else if (m.result === 'loss') {
      // Заглушка: пока в history нет поля «сколько потерял»,
      // считаем 0. Когда подключим transactions sum по war_*:loss —
      // обновим.
      totalUsdtLost += 0;
    }
  }

  const factionCounts: Record<string, number> = {};
  for (const m of wars) {
    factionCounts[m.faction] = (factionCounts[m.faction] || 0) + 1;
  }
  let topFaction = '—';
  let topCount = 0;
  for (const [f, c] of Object.entries(factionCounts)) {
    if (c > topCount) { topFaction = f; topCount = c; }
  }

  return {
    matches, wins, winrate, bestStreak,
    totalUsdtEarned, totalUsdtLost,
    netUsdt: totalUsdtEarned - totalUsdtLost,
    factionCounts, topFaction
  };
}

export function ProfileScreen() {
  const navigate = useNavigate();
  const { hapticTap } = useTelegram();
  const [tab, setTab] = useState<StatsTab>('season');
  const [settingsOpen, setSettingsOpen] = useState(false);

  // ФТУЕ.6: tooltip-флоу первого входа в Профиль.
  const tr = useTr();
  useTooltipFlow('profile', () => [
    {
      id: 'p-intro',
      coach: {
        title: tr('Профиль', 'Profile'),
        body: tr(
          'Тут твой ник, аватар, уровень аккаунта, лига сезона. Ниже — статы (сезон / lifetime) и плитки: чумбы, ачивки, рефералы, инвентарь.',
          "Your handle, avatar, account level, season league. Below — stats (season / lifetime) and tiles: chooms, achievements, referrals, inventory."
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'p-cosmetics',
      coach: {
        title: tr('Косметика', 'Cosmetics'),
        body: tr(
          'Тап по аватару — меняешь его (доступные открываются за уровень аккаунта или БП). Тап по титулу — переключаешь активный. Цвет ника — перк БП.',
          "Tap your avatar — switch it (unlocked via account level or BP). Tap a title — switch the active one. Handle color — BP perk."
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'p-settings',
      coach: {
        title: tr('Настройки и поддержка', 'Settings & support'),
        body: tr(
          'Значок «⚙» сверху справа — настройки (язык, звук, анимации, перезапуск туториала, удаление аккаунта). Иконка поддержки (?) — открывает чат с админом.',
          "«⚙» top-right — settings (language, sound, animations, restart tutorial, delete account). Support icon (?) — opens chat with admin."
        ),
        showNext: true, showSkip: false,
        nextLabel: tr('Понял', 'Got it')
      }
    }
  ]);
  const toggleStudy = useStudyMode(s => s.toggle);
  const studyActive = useStudyMode(s => s.active);

  // Все данные из глобального store (синхронизированы с Home).
  const player = useGameStore(s => s.player);
  const stats = useGameStore(s => s.stats);
  const counts = useGameStore(s => s.counts);
  const accentColor = useGameStore(s => s.profileAccentColor);
  const activeTitle = useGameStore(s => s.activeTitle);
  const avatarId = useGameStore(s => s.activeAvatarId);
  // ВОЙ.6: history для агрегации war-статов. Грузится один раз при
  // открытии профиля; обновляется при переключении табов через rerender.
  const [warStats, setWarStats] = useState<WarStats | null>(null);
  useEffect(() => {
    if (tab !== 'war' || warStats) return;
    let cancelled = false;
    getMatchHistory(100)
      .then((h) => { if (!cancelled) setWarStats(aggregateWarStats(h)); })
      .catch(() => { if (!cancelled) setWarStats(aggregateWarStats([])); });
    return () => { cancelled = true; };
  }, [tab, warStats]);
  // Для НЕ-war табов используем stats из store как раньше.
  const s = tab === 'war' ? null : stats[tab];
  // Модалка саппорта — вынесена из настроек на основной экран.
  const [supportOpen, setSupportOpen] = useState(false);

  // Telegram BackButton — пока пользователь на Profile, в шапке Telegram
  // показывается стрелка «назад», возвращающая на /home. В web-режиме no-op.
  const handleBack = useCallback(() => navigate('/home'), [navigate]);
  useBackButton(handleBack, true);

  // Tap-haptic для всех плиток.
  const tapAndNav = useCallback((path: string) => () => {
    hapticTap();
    navigate(path);
  }, [hapticTap, navigate]);

  return (
    <div className={styles.screen}>
      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <button className={`${styles.backBtn} cb`} onClick={() => navigate('/home')}>
            <span className={styles.chev}>‹</span> {tr('назад', 'back')}
          </button>
          <div className={`${styles.title} cd`}>{tr('ПРОФИЛЬ', 'PROFILE')}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className={`${styles.faqBtn} cut-s`}
              aria-label={tr('Режим изучения', 'Study mode')}
              onClick={() => { hapticTap(); toggleStudy(); }}
              data-tt-exit
              style={{
                background: studyActive ? 'var(--neon-cy)' : undefined,
                color: studyActive ? 'var(--bg)' : undefined,
                zIndex: studyActive ? 103 : undefined
              }}
            >?</button>
            <button
              className={`${styles.faqBtn} cut-s`}
              aria-label={tr('Настройки', 'Settings')}
              onClick={() => { hapticTap(); setSettingsOpen(true); }}
              style={{ fontSize: 14 }}
              data-tt="pr-settings"
            >⚙</button>
          </div>
        </div>

        {/* Profile card */}
        <div className={styles.profileSection}>
          <div className={`${styles.profileCard} cut-l`}>
            <div className={styles.profileRow}>
              <div
                className={styles.avatar}
                aria-label={tr('Аватар', 'Avatar')}
                data-tt="pr-avatar"
              >
                <UserAvatar
                  avatarId={avatarId}
                  size={80}
                />
                <div className={styles.levelBadge}>{player.level}</div>
              </div>
              <div className={styles.profileInfo}>
                <div className={styles.nickRow} data-tt="pr-nick">
                  <div className={`${styles.nick} cd`}>
                    <PlayerName
                      name={player.nick}
                      accentColor={accentColor || undefined}
                      title={activeTitle}
                      size="lg"
                    />
                  </div>
                </div>
                <div className={`${styles.league} cb`} data-tt="pr-league">{player.role}</div>
                <div className={styles.profileMeta}>
                  <div className={styles.metaItem} data-tt="pr-cup">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#FFB13D" strokeWidth="1.5">
                      <path d="M7 4h10v2a5 5 0 0 1-5 5 5 5 0 0 1-5-5V4z"/>
                      <path d="M7 5H4v2a3 3 0 0 0 3 3"/>
                      <path d="M17 5h3v2a3 3 0 0 1-3 3"/>
                      <path d="M9 19h6"/>
                      <path d="M12 11v8"/>
                    </svg>
                    <span className="cm">
                      {player.cupProgress.current}
                      <span className={styles.metaSub}>/{player.cupProgress.max}</span>
                    </span>
                  </div>
                  <div className={styles.metaItem} data-tt="pr-firewall">
                    <svg width="13" height="14" viewBox="0 0 24 26" fill="none" stroke="#FFB13D" strokeWidth="1.5">
                      <path d="M12 2l9 3v8c0 6-4 10-9 11-5-1-9-5-9-11V5z" fill="rgba(255,177,61,0.12)"/>
                      <path d="M12 8v8M9 12l3-2 3 2" strokeLinecap="round"/>
                    </svg>
                    <span className="cm" style={{ color: 'var(--neon-am)' }}>{player.firewalls}</span>
                  </div>
                  <div className={`${styles.regDate} cm`} data-tt="pr-regdate">{tr('с', 'since')} {player.regDate}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* УР.1: блок уровня аккаунта */}
        <AccountLevelBlock />

        {/* БП.1: секции выбора цвета ника и активного титула */}
        <ProfileBpSections />

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} cut-s ${tab === 'season' ? styles.tabActive : ''}`}
            onClick={() => setTab('season')}
            data-tt="pr-tab-season"
          >
            <span className={styles.tabLabel}>{tr('СЕЗОН', 'SEASON')}</span>
            <span className={styles.tabSub}>{tr('23 / 45 дн', '23 / 45 days')}</span>
          </button>
          <button
            className={`${styles.tab} cut-s ${tab === 'lifetime' ? styles.tabActive : ''}`}
            onClick={() => setTab('lifetime')}
            data-tt="pr-tab-lifetime"
          >
            <span className={styles.tabLabel}>{tr('ОБЩАЯ', 'LIFETIME')}</span>
            <span className={styles.tabSub}>{tr('за всё время', 'all-time')}</span>
          </button>
          {/* ВОЙ.6: новая вкладка статистики Войны Корп. */}
          <button
            className={`${styles.tab} cut-s ${tab === 'war' ? styles.tabActive : ''}`}
            onClick={() => setTab('war')}
            style={tab === 'war' ? { boxShadow: 'inset 0 0 0 1px var(--neon-rd, #FF3D3D)' } : undefined}
          >
            <span className={styles.tabLabel} style={tab === 'war' ? { color: 'var(--neon-rd, #FF3D3D)' } : undefined}>
              ⚔ {tr('КОРП-ВОЙНЫ', 'CORP WARS')}
            </span>
            <span className={styles.tabSub}>{tr('USDT-матчи', 'USDT matches')}</span>
          </button>
        </div>

        {/* Stats */}
        <div className={`${styles.stats} cut-m`}>
          {tab === 'war' ? (
            !warStats ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--t-mute)', fontSize: 11 }}>
                {tr('Загрузка…', 'Loading…')}
              </div>
            ) : warStats.matches === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--t-mute)', fontSize: 11, lineHeight: 1.6 }}>
                {tr(
                  'Войн корп пока не было. Сыграй первый USDT-матч — он появится здесь.',
                  "No Corp Wars yet. Play your first USDT match — it'll show here."
                )}
              </div>
            ) : (
              <div className={styles.statsGrid}>
                <StatCell value={String(warStats.matches)} label={tr('матчей в Войне', 'war matches')} />
                <StatCell value={`${warStats.winrate}%`} label="winrate" valueClass={styles.statValueGreen} />
                <StatCell value={String(warStats.bestStreak)} label={tr('лучшая серия', 'best streak')} />
                <StatCell
                  value={`$${warStats.totalUsdtEarned.toFixed(2)}`}
                  label={tr('заработано', 'earned')}
                  valueClass={styles.statValueGreen}
                />
                <StatCell
                  value={String(warStats.wins)}
                  label={tr('побед', 'wins')}
                  valueClass={styles.statValueText}
                />
                <StatCell
                  value={factionDisplayName(warStats.topFaction, tr)}
                  label={tr('любимая корпа', 'top corp')}
                  valueClass={`${styles.statValueFaction} ${styles[`statValueFaction${capitalize(warStats.topFaction)}`] || ''}`}
                />
              </div>
            )
          ) : s && (
            <div className={styles.statsGrid}>
              <StatCell value={String(s.matches)} label={tab === 'season' ? tr('матчей', 'matches') : tr('всего матчей', 'total matches')} />
              <StatCell value={`${s.winrate}%`} label="winrate" valueClass={styles.statValueGreen} />
              <StatCell value={String(s.streak)} label={tr('лучшая серия', 'best streak')} />
              <StatCell
                value={s.factionName}
                label={tab === 'season' ? tr('фракция сезона', 'season faction') : tr('любимая фракция', 'favourite faction')}
                valueClass={`${styles.statValueFaction} ${styles.statValueFactionDeus}`}
              />
              <StatCell value={s.hero} label={tr('лучший герой', 'best Hero')} valueClass={styles.statValueText} />
              <StatCell value={s.league} label={tr('макс. лига', 'peak league')} valueClass={styles.statValueText} />
            </div>
          )}
        </div>

        {/* 2×2 tiles */}
        <div className={styles.tiles}>
          <button
            className={`${styles.tile} ${styles.tileAmber} cut-m`}
            onClick={tapAndNav('/achievements')}
            data-tt="pr-t-achv"
          >
            <div className={styles.tileHead}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFB13D" strokeWidth="1.5">
                <path d="M7 4h10v2a5 5 0 0 1-5 5 5 5 0 0 1-5-5V4z"/>
                <path d="M9 19h6"/><path d="M12 11v8"/>
              </svg>
              <span className={styles.tileName}>{tr('Достижения', 'Achievements')}</span>
            </div>
            <div>
              <div className={styles.tileBody}>
                <span className={styles.tileCount}>
                  {counts.achievements.current} <span className={styles.tileCountTotal}>/ {counts.achievements.total}</span>
                </span>
                <span className={styles.tileExtra}>
                  {Math.round((counts.achievements.current / counts.achievements.total) * 100)}%
                </span>
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressBarFill}
                  style={{ width: `${(counts.achievements.current / counts.achievements.total) * 100}%` }}
                />
              </div>
            </div>
          </button>
          <button
            className={`${styles.tile} ${styles.tileCyan} cut-m`}
            onClick={tapAndNav('/friends')}
            data-tt="pr-t-friends"
          >
            <div className={styles.tileHead}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3DF0FF" strokeWidth="1.5">
                <circle cx="9" cy="8" r="3.5"/>
                <path d="M3 21a6 6 0 0 1 12 0"/>
                <circle cx="17" cy="9" r="3" opacity="0.6"/>
                <path d="M14 21a4 4 0 0 1 7 0" opacity="0.6"/>
              </svg>
              <span className={styles.tileName}>{tr('Чумбы', 'Chooms')}</span>
            </div>
            <div className={styles.tileBody}>
              <span className={styles.tileCount}>{counts.friends.count}</span>
              <div className={styles.onlineIndicator}>
                <div className={styles.onlineDot} />
                <span>{counts.friends.online} {tr('в сети', 'online')}</span>
              </div>
            </div>
          </button>
          <button
            className={`${styles.tile} ${styles.tileLime} cut-m`}
            onClick={tapAndNav('/referrals')}
            data-tt="pr-t-refs"
          >
            <div className={styles.tileHead}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CFF3D" strokeWidth="1.5">
                <circle cx="6" cy="12" r="2.5"/>
                <circle cx="18" cy="6" r="2.5"/>
                <circle cx="18" cy="18" r="2.5"/>
                <path d="M8 11l8-4M8 13l8 4"/>
              </svg>
              <span className={styles.tileName}>{tr('Рефералы', 'Referrals')}</span>
            </div>
            <div className={styles.tileBody}>
              <span className={styles.tileCount}>{counts.referrals.count}</span>
              <span className={styles.tileExtra}>+{counts.referrals.scripts} ◆ · {counts.referrals.ton} {tr('ШК', 'CC')}</span>
            </div>
          </button>
          <button
            className={`${styles.tile} ${styles.tileMag} cut-m`}
            onClick={tapAndNav('/inventory')}
            data-tt="pr-t-inv"
          >
            <div className={styles.tileHead}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF3D8F" strokeWidth="1.5">
                <path d="M5 7h14l-1 14H6z"/>
                <path d="M9 7V5a3 3 0 0 1 6 0v2"/>
              </svg>
              <span className={styles.tileName}>{tr('Инвентарь', 'Inventory')}</span>
            </div>
            <div className={styles.tileBody}>
              <span className={styles.tileCount}>{counts.inventory.items}</span>
              <span className={styles.tileExtra}>{tr('аватары · рамки', 'avatars · frames')}</span>
            </div>
          </button>
        </div>

        {/* Archive nav-row */}
        <button
          className={`${styles.navRow} cut-s`}
          onClick={tapAndNav('/archive')}
          data-tt="pr-archive"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A0A4B4" strokeWidth="1.5">
            <path d="M4 4h12l4 4v12a2 2 0 0 1-2 2H4z"/>
            <path d="M14 4v6h6"/>
            <path d="M8 14h8M8 18h6"/>
          </svg>
          <div className={styles.navInfo}>
            <div className={styles.navName}>{tr('Архив матчей', 'Match archive')}</div>
            <div className={styles.navDesc}>{tr('последний: 06/05/26 · 22:30 · победа', 'last: 06/05/26 · 22:30 · win')}</div>
          </div>
          <span className={styles.navChevron}>›</span>
        </button>

        {/* Поддержка nav-row — открывает SupportModal */}
        <button
          className={`${styles.navRow} cut-s`}
          onClick={() => { hapticTap(); setSupportOpen(true); }}
          data-tt="pr-support"
          style={{ marginTop: 6 }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF3D8F" strokeWidth="1.5">
            <path d="M4 5h16v12H5l-3 3z"/>
            <path d="M8 9h8M8 13h5"/>
          </svg>
          <div className={styles.navInfo}>
            <div className={styles.navName}>{tr('Написать в поддержку', 'Contact support')}</div>
            <div className={styles.navDesc}>{tr('создать тикет / посмотреть свои', 'create ticket / view yours')}</div>
          </div>
          <span className={styles.navChevron}>›</span>
        </button>
      </div>

      {/* Модалка саппорта на уровне экрана. */}
      {supportOpen && <SupportModal onClose={() => setSupportOpen(false)} />}

      {/* === Модалка настроек === */}
      {settingsOpen && (
        <InfoModal title={tr('НАСТРОЙКИ', 'SETTINGS')} onClose={() => setSettingsOpen(false)}>
          <SettingsContent />
        </InfoModal>
      )}
    </div>
  );
}

// Настройки игрока — соответствует profile-prototype.html (settings_modal).
function SettingsContent() {
  const { hapticTap, hapticSuccess, hapticError } = useTelegram();
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const toast = useToast(s => s.show);
  const deletionScheduledAt = useGameStore(s => s.player.deletionScheduledAt);
  const [musicVol, setMusicVol] = useState(() => {
    const v = localStorage.getItem('corps.musicVolume'); // 0..100
    if (v != null && v !== '') return Number(v);
    return localStorage.getItem('corps.music') !== '0' ? 30 : 0; // миграция со старого тогла
  });
  const [sfxVol, setSfxVol] = useState(() => {
    const v = localStorage.getItem('corps.sfxVolume'); // 0..100
    if (v != null && v !== '') return Number(v);
    return localStorage.getItem('corps.sfx') !== '0' ? 60 : 0; // миграция со старого тогла
  });
  const [anim, setAnim] = useState(() => localStorage.getItem('corps.anim') !== '0');
  const [enemyEmotes, setEnemyEmotes] = useState(() => localStorage.getItem('corps.enemyEmotes') !== '0');

  const setStored = (key: string, val: string) => {
    try { localStorage.setItem(key, val); } catch { /* ignore */ }
  };
  const toggle = (key: string, cur: boolean, setter: (v: boolean) => void) => {
    hapticTap();
    const next = !cur;
    setter(next);
    setStored(`corps.${key}`, next ? '1' : '0');
    if (key === 'anim') {
      document.body.classList.toggle('no-anim', !next);
      // ОТК.1: оповещаем потребителей хука useAnimationsEnabled (CrateOpeningScene).
      import('../hooks/useAnimationsEnabled').then(m => m.notifyAnimationsChanged());
    }
  };

  // Этап 63: громкость музыки меню (ползунок 0..100). Применяется мгновенно.
  const onMusicVol = (v: number) => {
    setMusicVol(v);
    setStored('corps.musicVolume', String(v));
    setStored('corps.music', v > 0 ? '1' : '0'); // совместимость со старым ключом
    import('../audio/music').then(m => m.setMusicVolume(v / 100));
  };

  // Этап 67: громкость звуков эффектов (ползунок 0..100, 0 = выкл). Применяется
  // мгновенно + короткий «тап» для предпрослушки уровня при перетаскивании.
  const onSfxVol = (v: number) => {
    setSfxVol(v);
    setStored('corps.sfxVolume', String(v));
    setStored('corps.sfx', v > 0 ? '1' : '0'); // совместимость со старым тоглом
    setSfxVolume(v / 100);
    playSfx('tap');
  };

  // Запрос удаления аккаунта с тройным подтверждением.
  // Фраза-подтверждение для EN — «delete my account» (по-английски).
  const deleteConfirmPhrase = isEn ? 'delete my account' : 'удалить мой аккаунт';
  const onRequestDelete = async () => {
    hapticTap();
    // 1) Первое подтверждение
    if (!window.confirm(tr(
      'Удалить аккаунт?\n\n' +
      'Все Герои, карты, Скрипты, USDT, БП, реф-история и колоды будут удалены через 7 дней.\n\n' +
      'В течение этих 7 дней можно отменить.',
      'Delete account?\n\n' +
      'All Heroes, cards, Scripts, USDT, BP, referral history and decks will be deleted in 7 days.\n\n' +
      'You can cancel within those 7 days.'
    ))) return;
    // 2) Второе подтверждение
    if (!window.confirm(tr(
      'Точно? Тебе придётся начинать с нуля.\n\n' +
      'Это последнее предупреждение перед вводом подтверждения.',
      'Really? You\'ll have to start over from scratch.\n\n' +
      'This is the last warning before the confirmation phrase.'
    ))) return;
    // 3) Ввод фразы
    const phrase = window.prompt(tr(
      'Введи фразу «удалить мой аккаунт» (целиком и точно):',
      'Type the phrase «delete my account» (exactly):'
    ));
    if (phrase?.trim().toLowerCase() !== deleteConfirmPhrase) {
      hapticError();
      toast(tr('Фраза не совпала. Удаление отменено.', 'Phrase didn\'t match. Deletion cancelled.'), 'amber');
      return;
    }
    try {
      const r = await requestAccountDeletion();
      hapticSuccess();
      const date = new Date(r.deletionScheduledAt).toLocaleDateString(isEn ? 'en-US' : 'ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
      toast(tr(
        `Удаление запланировано на ${date}. В Настройках можно отменить.`,
        `Deletion scheduled for ${date}. You can cancel in Settings.`
      ), 'red', 6000);
      // Обновляем store — чтобы UI сразу показал баннер.
      useGameStore.setState(s => ({ player: { ...s.player, deletionScheduledAt: r.deletionScheduledAt } }));
    } catch (e: any) {
      hapticError();
      // Этап 69: сервер не даёт удалить при активном эскроу (Война/лоты) —
      // объясняем причину, чтобы игрок не потерял замороженные средства.
      if (e?.payload?.error === 'escrow_active') {
        toast(tr(
          'Нельзя удалить аккаунт: есть замороженные средства в Войне или активные лоты на маркете. Заверши их и попробуй снова.',
          'Can\'t delete account: you have frozen War stakes or active marketplace listings. Finish them and try again.'
        ), 'amber', 7000);
        return;
      }
      toast(tr(`Не удалось: ${e?.message || 'ошибка сервера'}`, `Failed: ${e?.message || 'server error'}`), 'red');
    }
  };

  // Отмена запланированного удаления.
  const onCancelDelete = async () => {
    hapticTap();
    try {
      await cancelAccountDeletion();
      hapticSuccess();
      toast(tr('Удаление отменено. Аккаунт сохранён.', 'Deletion cancelled. Account saved.'), 'green');
      useGameStore.setState(s => ({ player: { ...s.player, deletionScheduledAt: null } }));
    } catch (e: any) {
      hapticError();
      toast(tr(`Не удалось: ${e?.message || 'ошибка сервера'}`, `Failed: ${e?.message || 'server error'}`), 'red');
    }
  };

  return (
    <div>
      <SettingRow
        icon="🌐"
        label={tr('Язык интерфейса', 'Interface language')}
        sub={tr('EN частично — переводы в работе', 'EN partial — translations in progress')}
        control={<div style={{ width: 200 }}><LanguagePicker /></div>}
      />
      <SettingRow
        icon="🎵"
        label={tr('Музыка', 'Music')}
        control={<VolumeSlider value={musicVol} onChange={onMusicVol} />}
      />
      <SettingRow
        icon="🔊"
        label={tr('Звуки эффектов', 'Sound effects')}
        control={<VolumeSlider value={sfxVol} onChange={onSfxVol} />}
      />
      <SettingRow
        icon="✨"
        label={tr('Анимации интерфейса', 'UI animations')}
        sub={tr('отключи если игра тормозит', 'turn off if the game lags')}
        control={<Toggle on={anim} onClick={() => toggle('anim', anim, setAnim)} />}
      />
      <SettingRow
        icon="😈"
        label={tr('Эмоции противника', 'Opponent emotes')}
        sub={tr('скрыть бесплатные · платные остаются', 'hide free · paid stay visible')}
        control={<Toggle on={enemyEmotes} onClick={() => toggle('enemyEmotes', enemyEmotes, setEnemyEmotes)} />}
      />
      {/* ФТУЕ.1: перезапуск туториала. Сбрасывает ftue_status в 'pending'
          и перезагружает страницу — Pre-FTUE покажется снова. Инвентарь,
          Скр, БП, титулы, ачивки и пр. — НЕ трогаются. */}
      <SettingRow
        icon="🎓"
        label={tr('Перезапустить обучение', 'Restart tutorial')}
        sub={tr('Pre-FTUE с нуля · инвентарь не теряется', 'Pre-FTUE from scratch · inventory kept')}
        control={
          <button
            onClick={async () => {
              hapticTap();
              if (!window.confirm(tr(
                'Сбросить прохождение обучения и пройти его заново? Инвентарь и валюта останутся.',
                'Reset tutorial progress and run it again? Inventory and currency are kept.'
              ))) return;
              try {
                await setFtueState('restart');
                hapticSuccess();
                toast(tr('Туториал сброшен. Перезагружаю...', 'Tutorial reset. Reloading...'), 'green');
                setTimeout(() => window.location.reload(), 600);
              } catch (e: any) {
                hapticError();
                toast(tr(`Не удалось: ${e?.message || 'ошибка'}`, `Failed: ${e?.message || 'error'}`), 'red');
              }
            }}
            style={{
              padding: '6px 12px',
              background: 'transparent',
              border: '0.5px solid var(--neon-cy)',
              color: 'var(--neon-cy)',
              fontSize: 11,
              fontFamily: 'inherit',
              letterSpacing: 1,
              cursor: 'pointer'
            }}
          >
            {tr('ЗАПУСТИТЬ', 'RUN')}
          </button>
        }
      />
      {/* ФТУЕ.2 (dev): прямой переход на /tutorial-demo для визуальной
          проверки спотлайт/коуч/палец-индикатора. Не виден в готовом
          UI — это служебная ссылка из TG WebView (там нет адресной
          строки). Когда FTUE стабилизируется — кнопку уберём. */}
      <SettingRow
        icon="🧪"
        label={tr('DEV · Туториал-демо', 'DEV · Tutorial demo')}
        sub={tr('прогон 5 примитивов оверлея', 'run-through of 5 overlay primitives')}
        control={
          <button
            onClick={() => { hapticTap(); window.location.href = '/tutorial-demo'; }}
            style={{
              padding: '6px 12px',
              background: 'transparent',
              border: '0.5px solid var(--t-lo)',
              color: 'var(--t-md)',
              fontSize: 11,
              fontFamily: 'inherit',
              letterSpacing: 1,
              cursor: 'pointer'
            }}
          >
            {tr('ОТКРЫТЬ', 'OPEN')}
          </button>
        }
      />
      {/* ПУШ.1: расширенный блок «Уведомления» — категории по тогглам. */}
      <NotificationsBlock />
      <div style={{ marginTop: 12, fontSize: 10, color: 'var(--t-lo)', textAlign: 'center' }}>
        {tr('Клиент', 'Client')}: v0.1 React · {tr('Движок', 'Engine')}: C.1-WIP
      </div>

      {/* Удаление аккаунта */}
      <div style={{ marginTop: 16 }}>
        {deletionScheduledAt ? (
          <div style={{
            padding: '10px 12px',
            background: 'rgba(255,80,80,0.08)',
            border: '0.5px solid #ff5252',
            color: '#ff7a7a',
            fontSize: 12,
            lineHeight: 1.4
          }}>
            <div style={{ marginBottom: 6, fontWeight: 700 }}>
              ⚠ {tr('Удаление запланировано на', 'Deletion scheduled for')} {new Date(deletionScheduledAt).toLocaleDateString(isEn ? 'en-US' : 'ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </div>
            <div style={{ color: 'var(--t-md)', fontSize: 11, marginBottom: 8 }}>
              {tr(
                'После этой даты при первом входе аккаунт будет удалён полностью. До этого можно отменить.',
                'After that date, on first login the account will be fully deleted. Until then you can cancel.'
              )}
            </div>
            <button onClick={onCancelDelete} style={{
              width: '100%',
              padding: '8px',
              background: 'var(--neon-li)',
              color: 'var(--bg)',
              border: 'none',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1,
              cursor: 'pointer',
              fontFamily: 'inherit'
            }}>
              {tr('ОТМЕНИТЬ УДАЛЕНИЕ', 'CANCEL DELETION')}
            </button>
          </div>
        ) : (
          <button onClick={onRequestDelete} style={deleteBtnStyle}>
            {tr('УДАЛИТЬ АККАУНТ', 'DELETE ACCOUNT')}
          </button>
        )}
      </div>

    </div>
  );
}

const deleteBtnStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px',
  background: 'transparent',
  color: '#ff7a7a',
  border: '0.5px solid #ff7a7a',
  fontSize: 11,
  cursor: 'pointer',
  fontFamily: 'inherit',
  letterSpacing: 1
};

function SettingRow({
  icon, label, sub, control, clickable, onClick
}: {
  icon?: string;
  label: string;
  sub?: string;
  control?: React.ReactNode;
  clickable?: boolean;
  onClick?: () => void;
}) {
  const style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 0',
    cursor: clickable ? 'pointer' : 'default',
    color: 'inherit',
    background: 'transparent',
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: '0.5px solid var(--line)',
    width: '100%',
    textAlign: 'left',
    fontFamily: 'inherit'
  };
  const inner = (
    <>
      {icon && <span style={{ fontSize: 18, flexShrink: 0, width: 22, textAlign: 'center' }}>{icon}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--t-hi)' }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: 'var(--t-lo)', marginTop: 2 }}>{sub}</div>}
      </div>
      {control && <div style={{ flexShrink: 0 }}>{control}</div>}
    </>
  );
  if (clickable) {
    return <button type="button" onClick={onClick} style={style}>{inner}</button>;
  }
  return <div style={style}>{inner}</div>;
}

// Этап 63: ползунок громкости (для музыки меню). 0..100 + подпись «N%».
function VolumeSlider({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: 160 }}>
      <input
        type="range"
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label="Music volume"
        style={{ flex: 1, accentColor: 'var(--neon-li)', cursor: 'pointer', height: 4 }}
      />
      <span style={{ fontSize: 11, color: 'var(--t-md)', width: 32, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {value}%
      </span>
    </div>
  );
}

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: 42,
        height: 22,
        background: on ? 'var(--neon-li)' : 'var(--bg)',
        border: `0.5px solid ${on ? 'var(--neon-li)' : 'var(--line)'}`,
        borderRadius: 11,
        position: 'relative',
        cursor: 'pointer',
        padding: 0,
        transition: 'background 150ms'
      }}
    >
      <div style={{
        width: 16,
        height: 16,
        background: on ? 'var(--bg)' : 'var(--t-md)',
        borderRadius: '50%',
        position: 'absolute',
        top: 2,
        left: on ? 22 : 2,
        transition: 'left 150ms'
      }} />
    </button>
  );
}

function StatCell({
  value, label, valueClass = ''
}: { value: string; label: string; valueClass?: string }) {
  return (
    <div className={styles.statCell}>
      <span className={`${styles.statValue} ${valueClass}`}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

// БП.1: секция выбора цвета ника. Прозвища выбираются в Инвентаре.
// Плашка цвета видна всегда; если у юзера нет перка nickname_color —
// при клике на не-белый показываем toast «Чумба, для этого нужен Пропуск».
//
// При маунте подгружаем /api/battle-pass/current — иначе на первом
// заходе bp=null и hasColorPerk=false, даже если перк есть. Это был
// race: первый клик ругался, повторный работал.
function ProfileBpSections() {
  const { hapticTap, hapticSuccess, hapticError } = useTelegram();
  const tr = useTr();
  const toast = useToast(s => s.show);
  const accent = useGameStore(s => s.profileAccentColor);
  const bp = useGameStore(s => s.battlePass);
  const setAccent = useGameStore(s => s.setProfileAccentColor);
  const setBattlePass = useGameStore(s => s.setBattlePass);

  // Preload battlePass, чтобы знать про перки сразу при открытии Profile.
  useEffect(() => {
    if (bp) return;
    (async () => {
      try {
        const data = await getBattlePass();
        setBattlePass(data as any);
      } catch { /* сервер недоступен — оставляем null */ }
    })();
  }, [bp, setBattlePass]);

  const hasColorPerk = !!bp?.unlocked_perks?.includes('nickname_color');

  const onPickColor = async (color: string | null) => {
    hapticTap();
    // Без перка nickname_color не-белый цвет недоступен.
    if (color !== null && !hasColorPerk) {
      hapticError();
      toast(tr('Чумба, для этого нужен Пропуск', 'Choom, you need the Pass for that'), 'amber', 4000);
      return;
    }
    try {
      // Сервер хранит без '#'. null = сбросить.
      await setAccentColor(color);
      setAccent(color);
      hapticSuccess();
    } catch (e: any) {
      hapticError();
      toast(tr(`Не удалось сменить цвет: ${e?.message || 'ошибка'}`, `Couldn't change color: ${e?.message || 'error'}`), 'red');
    }
  };

  return (
    <div style={{ margin: '8px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* ЦВЕТ НИКА — плашка показывается всегда. Если перка нет, при клике
          по не-белому идёт toast «Чумба, для этого нужен Пропуск». */}
      <div className="cut-s" style={{ padding: 10, background: 'rgba(255,255,255,0.02)' }}>
        <div style={{
          fontSize: 11, color: 'var(--t-md)', marginBottom: 8,
          letterSpacing: 1, display: 'flex', justifyContent: 'space-between'
        }}>
          <span>{tr('ЦВЕТ НИКА', 'HANDLE COLOR')}</span>
          {!hasColorPerk && (
            <span style={{ color: 'var(--neon-am)' }}>{tr('требует БП', 'requires BP')}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ACCENT_PALETTE.map(c => {
            const isLocked = !hasColorPerk && c !== 'FFFFFF';
            return (
              <button
                key={c}
                onClick={() => onPickColor(c === 'FFFFFF' ? null : c)}
                style={{
                  width: 26, height: 26,
                  background: `#${c}`,
                  border: `1.5px solid ${accent === c || (!accent && c === 'FFFFFF') ? '#fff' : 'transparent'}`,
                  cursor: 'pointer',
                  padding: 0,
                  opacity: isLocked ? 0.45 : 1,
                  position: 'relative'
                }}
                aria-label={tr(`Цвет ника #${c}`, `Handle color #${c}`)}
                title={isLocked ? tr('Нужен Боевой Пропуск', 'Battle Pass required') : undefined}
              />
            );
          })}
          <button
            onClick={() => onPickColor(null)}
            style={{
              padding: '0 8px', fontSize: 10,
              background: 'transparent',
              color: 'var(--t-md)',
              border: '0.5px solid var(--t-md)',
              cursor: 'pointer',
              fontFamily: 'inherit'
            }}
          >{tr('Сбросить', 'Reset')}</button>
        </div>
      </div>
      {/* Выбор активного титула — теперь в Инвентаре → раздел «Прозвища». */}
    </div>
  );
}

// УР.1: блок уровня аккаунта с XP-баром и описанием следующей награды.
function AccountLevelBlock() {
  const tr = useTr();
  const level = useGameStore(s => s.player.level);
  const xpInLevel = useGameStore(s => s.accountXpInLevel);
  const xpToNext = useGameStore(s => s.accountXpToNext);
  const nextReward = useGameStore(s => s.nextLevelReward);

  const total = xpInLevel + xpToNext;
  const pct = total > 0 ? Math.floor((xpInLevel / total) * 100) : 100;

  return (
    <div className="cut-s" data-tt="pr-account-level" style={{
      margin: '8px 0',
      padding: 12,
      background: 'rgba(255,255,255,0.03)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: 'var(--t-md)', letterSpacing: 1 }}>{tr('УРОВЕНЬ АККАУНТА', 'ACCOUNT LEVEL')}</span>
        <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--neon-am)' }}>{level}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--t-md)', marginBottom: 3 }}>
        <span>{tr(`прогресс до ур. ${level + 1}`, `progress to lvl ${level + 1}`)}</span>
        <span style={{ color: 'var(--neon-cy)', fontWeight: 700 }}>{pct}%</span>
      </div>
      <div style={{
        height: 8,
        background: 'rgba(255,255,255,0.06)',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: 'var(--neon-cy)',
          transition: 'width 200ms'
        }} />
      </div>
      <div style={{ fontSize: 10, color: 'var(--t-md)', marginTop: 4 }}>
        {xpInLevel} / {total} XP {total > 0 && <>· {tr('ещё', 'left')} <strong>{xpToNext}</strong></>}
      </div>
      {nextReward && (
        <div style={{
          marginTop: 10,
          paddingTop: 8,
          borderTop: '0.5px solid var(--line)',
          fontSize: 11,
          color: 'var(--t-md)'
        }}>
          <span style={{ color: 'var(--neon-am)' }}>{tr(`След. награда (ур. ${nextReward.level}):`, `Next reward (lvl ${nextReward.level}):`)}</span>{' '}
          <span style={{ color: 'var(--t-hi)' }}>{nextReward.description}</span>
        </div>
      )}
    </div>
  );
}

// ПУШ.1: тогглы категорий Telegram-уведомлений.
function NotificationsBlock() {
  const { hapticTap, hapticSuccess, hapticError } = useTelegram();
  const tr = useTr();
  const toast = useToast(s => s.show);
  const [settings, setSettings] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { getNotificationSettings } = await import('../api/client');
        const r = await getNotificationSettings();
        setSettings(r.settings || {});
      } catch { /* offline */ }
      setLoaded(true);
    })();
  }, []);

  const toggleCategory = async (cat: string) => {
    hapticTap();
    // Дефолт = true. Если уже false → переключаем на true и обратно.
    const cur = settings[cat] !== false;
    const next = { ...settings, [cat]: !cur };
    setSettings(next);
    try {
      const { patchNotificationSettings } = await import('../api/client');
      await patchNotificationSettings(next as any);
      hapticSuccess();
    } catch (e: any) {
      hapticError();
      toast(tr(`Не сохранилось: ${e?.message || 'ошибка'}`, `Didn't save: ${e?.message || 'error'}`), 'red');
      setSettings(settings); // rollback
    }
  };

  const categories: { key: string; label: string; sub?: string }[] = [
    { key: 'support_reply',         label: tr('Ответы саппорта', 'Support replies'),         sub: tr('когда саппортер ответил в твой тикет', 'when support replied to your ticket') },
    { key: 'support_closed',        label: tr('Закрытие тикета', 'Ticket closed') },
    { key: 'level_up',              label: tr('Повышение уровня', 'Level up') },
    { key: 'achievement',           label: tr('Новые достижения', 'New achievements') },
    { key: 'daily_login_reminder',  label: tr('Напоминание о входе', 'Login reminder'),      sub: tr('один пуш в день если не зашёл', 'one push a day if you skipped') },
    { key: 'admin_grant',           label: tr('Выдачи от админа', 'Admin grants'),           sub: tr('грант скриптов / USDT / ШК', 'scripts / USDT / Cipher Crate grant') },
    { key: 'friend_request',        label: tr('Запросы в чумбы', 'Choom requests'),          sub: tr('когда кто-то хочет добавить тебя в друзья', "when someone wants to add you as a choom") },
    { key: 'market',                label: tr('Маркет', 'Market'),                            sub: tr('покупка/продажа/ставки', 'buy / sell / bids') },
    { key: 'system',                label: tr('Системные', 'System')            }
  ];

  return (
    <>
      <div style={{
        marginTop: 14, marginBottom: 6,
        fontSize: 10, color: 'var(--t-md)', letterSpacing: 1
      }}>
        {tr('УВЕДОМЛЕНИЯ TELEGRAM', 'TELEGRAM NOTIFICATIONS')}
      </div>
      {!loaded ? (
        <div style={{ fontSize: 11, color: 'var(--t-lo)', padding: 8 }}>{tr('Загрузка…', 'Loading…')}</div>
      ) : (
        categories.map(c => {
          const on = settings[c.key] !== false; // default true
          return (
            <SettingRow
              key={c.key}
              icon="🔔"
              label={c.label}
              sub={c.sub}
              control={<Toggle on={on} onClick={() => toggleCategory(c.key)} />}
            />
          );
        })
      )}
      <div style={{ fontSize: 9, color: 'var(--t-lo)', marginTop: 4, textAlign: 'center' }}>
        {tr(
          'Не влияет на in-game уведомления (бейдж на иконке писем).',
          "Doesn't affect in-game notifications (mail icon badge)."
        )}
      </div>
    </>
  );
}
